import { inject, Injectable } from "@angular/core";
import { ComparatorOperators, GetQualitativeInputGQL, GetQualitativeInputQuery, LegalEntityFields, LegalEntitySimpleQueryInput, OperatorInput, OrderTypeInput, QueryLegalEntitiesByQueryComplexArgs, RiskMapTreeQueryComplexInput, RiskMapTreeSortInput, UpdateQualitativeInputGQL, UpdateQualitativeInputTreeGQL, UpdateQualitativeInputTreeMutation, UpdateQualitativeQuestionInput, DownloadQualitativeInputTreeGQL, DownloadQualitativeInputTreeMutation, CommonProcessTreeColumns, RiskMapTreeColumns, RiskMapTreeSimpleQueryInput, PagingInput, GetRiskMapTreeFiltersQuery, GetRiskMapTreeFiltersGQL, UpdateTreeNoteQualitativeInputGQL } from "../../../graphql/generated";
import { map, Observable } from "rxjs";
import { MutationData, unwrapMutationData } from "../shared/graphql-mutation-response.util";
@Injectable({
    providedIn: 'root'
})

export class QualitativeInputTreeService {
    private readonly GetQualitativeInputGQL: GetQualitativeInputGQL = inject(GetQualitativeInputGQL);
    private readonly UpdateQualitativeInputTreeGQL: UpdateQualitativeInputTreeGQL = inject(UpdateQualitativeInputTreeGQL);
    private readonly DownloadQualitativeInputTreeGQL: DownloadQualitativeInputTreeGQL = inject(DownloadQualitativeInputTreeGQL);
    private readonly GetRiskMapTreeFiltersGQL: GetRiskMapTreeFiltersGQL = inject(GetRiskMapTreeFiltersGQL);
    private readonly UpdateTreeNoteQualitativeInputGQL: UpdateTreeNoteQualitativeInputGQL = inject(UpdateTreeNoteQualitativeInputGQL)

    getQualitative(
        campaignId: string,
        legalEntityId: string,
        completed: boolean,
        parameters: any,
        au: boolean | null
    ): Observable<GetQualitativeInputQuery['riskMapQualitativeTreeByLegalEntityIdAndCampaignId']> {
        const param = convertLazyLoadToComplexWithPageQualitativeInput(parameters);
        return this.GetQualitativeInputGQL.fetch({ campaignId, legalEntityId, completed, query: param?.query as RiskMapTreeQueryComplexInput, sort: param?.sort as RiskMapTreeSortInput, au, page: param.page }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.riskMapQualitativeTreeByLegalEntityIdAndCampaignId))
    }

    getAllQualitative(
        campaignId: string,
        legalEntityId: string,
        au: boolean | null
    ): Observable<GetRiskMapTreeFiltersQuery['riskMapTreeFields']> {
        return this.GetRiskMapTreeFiltersGQL.fetch({ campaignId, legalEntityId, au: null, active: true }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.riskMapTreeFields))
    }

    updateQualitativeInput(
        id: string,
        questionId: string,
        opinionId: string,
        comments: string,
        filters: boolean
    ): Observable<MutationData<UpdateQualitativeInputTreeMutation['updateQualitativeInputTreeQuestion']>> {
        const updateQualitativeInput: UpdateQualitativeQuestionInput = {
            comments,
            id, opinionId, questionId
        }
        return this.UpdateQualitativeInputTreeGQL.mutate({ updateQualitativeInput, filters }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateQualitativeInputTreeQuestion), unwrapMutationData('Unable to update the qualitative input'))
    }

    updateQualitativeInputMultiple(
        updateQualitativeInput: UpdateQualitativeQuestionInput[], filters: boolean
    ): Observable<MutationData<UpdateQualitativeInputTreeMutation['updateQualitativeInputTreeQuestion']>> {
        return this.UpdateQualitativeInputTreeGQL.mutate({ updateQualitativeInput, filters }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateQualitativeInputTreeQuestion), unwrapMutationData('Unable to update the qualitative input'))
    }

    // createQualitativeInput(
    //     cptId: string,
    //     campaignId: string,
    //     legalEntityId: string,
    //     opinionId: string,
    //     questionId: string,
    //     comments: string
    // ): Observable<CreateQualitativeInputTreeMutation['createQualitativeInputTreeQuestion']> {
    //     return this.CreateQualitativeInputTreeGQL.mutate({ campaignId, comments, cptId, legalEntityId, opinionId, questionId }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.createQualitativeInputTreeQuestion))
    // }

    downloadQualitativeInputTree(
        campaignId: string,
        completed: boolean | null,
        legalEntityId: string,
        parameters: any,
        timeZone: string
    ): Observable<DownloadQualitativeInputTreeMutation['downloadQualitativeInputTree']> {
        const param = parameters ? convertLazyLoadToComplexWithPageQualitativeInput(parameters) : null;
        return this.DownloadQualitativeInputTreeGQL.mutate({ campaignId, completed, legalEntityId, timeZone, query: param?.query as RiskMapTreeQueryComplexInput, sort: param?.sort as RiskMapTreeSortInput }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.downloadQualitativeInputTree))
    }

    updateTreeNote(
        campaignId: string,
        legalEntityId: string,
        processCode: string,
        note: string,
        filter: boolean
    ): Observable<MutationData<UpdateQualitativeInputTreeMutation['updateQualitativeInputTreeQuestion']>> {
        return this.UpdateTreeNoteQualitativeInputGQL.mutate({ campaignId, legalEntityId, processCode, note, filter }, { fetchPolicy: 'no-cache' }).pipe(map(res => (res.data?.updateTreeNote)), unwrapMutationData('Unable to update the note'))
    }
}

export function convertLazyLoadToComplexWithPageQualitativeInput(parameters: any): {
    query: RiskMapTreeQueryComplexInput
    sort: RiskMapTreeSortInput,
    page: PagingInput
} {
    const query: RiskMapTreeSimpleQueryInput[] = [];
    if (parameters) {

        if (parameters.filters) {

            Object.entries(parameters.filters).forEach(([field, values]) => {
                //console.log"value", values);

                if (values !== null && Array.isArray(values) && values.length > 0) {
                    (ENUM_CPT_TREE[field as keyof typeof ENUM_CPT_TREE] || [field]).forEach(key => {

                        query.push({
                            field: key as RiskMapTreeColumns,
                            filters: {
                                operator: key === RiskMapTreeColumns.Level ? ComparatorOperators.Eq : ComparatorOperators.Match,
                                value: key !== RiskMapTreeColumns.Au ? values : values.map(v => v?.toLowerCase() === 'active' || v?.toLowerCase() === 'yes' ? true : false)
                            }
                        });
                    })
                }
            });
        }
    }
    return {
        query: {
            operator: OperatorInput.And,
            query: [
                {
                    query: query,
                    operator: OperatorInput.And
                }
            ]
        },
        sort: {
            sortField: ENUM_CPT_TREE?.[parameters?.order?.sortField as keyof typeof ENUM_CPT_TREE]?.[0] as RiskMapTreeColumns || RiskMapTreeColumns.Sort,
            sortOrder: parameters?.order?.sortDirection === 'ASC' ? OrderTypeInput.Asc : OrderTypeInput.Desc
        },
        page: {
            pageNo: parameters.page,
            pageSize: parameters.rows
        }
    }
}

export const ENUM_CPT_TREE = {
    name: [RiskMapTreeColumns.Name],
    level: [RiskMapTreeColumns.Level],
    code: [RiskMapTreeColumns.Code],
    cluster: [RiskMapTreeColumns.Cluster],
    internalAuditDomain: [RiskMapTreeColumns.InternalAuditDomain],
    processStatus: [RiskMapTreeColumns.Status],
    valueChain: [RiskMapTreeColumns.ValueChain],
    note: [RiskMapTreeColumns.Note],
    processNote: [RiskMapTreeColumns.Note],
    au: [RiskMapTreeColumns.Au],
    auditableUniverse: [RiskMapTreeColumns.Au],
    status: [RiskMapTreeColumns.RiskMapStatus],
    riskMapStatus: [RiskMapTreeColumns.RiskMapStatus],
    riskParameterName: ['RISK_PARAMETER_NAME'],
    auditArea: [RiskMapTreeColumns.AuditDomain],
    auditSubArea: [RiskMapTreeColumns.AuditSubdomain],
    sort: [RiskMapTreeColumns.Sort]
}